import React, { ReactElement, useEffect, useState } from "react";
import { InstrumentTypeResponse } from "../../codegen-api";
import {
  getDefaultInstrumentType,
  isInstrumentTypeEnabled,
} from "../../config/features";
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import { AssetResponse } from "../../utils/asset";
import {
  getChainScopedStorageKey,
  getDefaultTradingAsset,
} from "../../utils/defaultMarket";

export interface IMarket {
  asset: AssetResponse;
  derivative: InstrumentTypeResponse;
}

interface IMarketContextType {
  market: IMarket;
  setMarket: React.Dispatch<React.SetStateAction<IMarket>>;
  showCommandModal: boolean;
  setShowCommandModal: React.Dispatch<React.SetStateAction<boolean>>;
}

interface IMarketContextProviderProps {
  children: ReactElement;
}

export const MarketContext = React.createContext<IMarketContextType>({
  market: {
    asset: getDefaultTradingAsset(),
    derivative: getDefaultInstrumentType(),
  },
  setMarket: () => {},
  showCommandModal: false,
  setShowCommandModal: () => {},
});

/**
 * The persisted market is untrusted input: it can hold a vertical that has
 * since been switched off (or a value that was never valid). Anything whose
 * trading type is not currently enabled is dropped rather than restored, so a
 * disabled vertical can never come back through localStorage.
 */
const getLastViewedMarket = (): IMarket | undefined => {
  let item: string | null = null;
  try {
    item = localStorage.getItem(
      getChainScopedStorageKey(LocalStorageKeyEnum.LAST_VIEWED_MARKET)
    );
  } catch {
    return undefined;
  }
  if (!item) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(item) as Partial<IMarket> | null;
    // `derivative` is whatever was in storage — possibly a vertical that has
    // since been switched off, or a string that was never a valid instrument
    // type. `isInstrumentTypeEnabled` rejects both without throwing; the whole
    // read stays inside the try so a surprise can never break the render.
    if (!parsed?.asset || !isInstrumentTypeEnabled(parsed.derivative)) {
      return undefined;
    }
    return {
      asset: parsed.asset,
      derivative: parsed.derivative as InstrumentTypeResponse,
    };
  } catch {
    return undefined;
  }
};

const setLastViewedMarket = (market: IMarket) => {
  localStorage.setItem(
    getChainScopedStorageKey(LocalStorageKeyEnum.LAST_VIEWED_MARKET),
    JSON.stringify(market)
  );
};

export function MarketContextProvider({
  children,
}: IMarketContextProviderProps) {
  const [showCommandModal, setShowCommandModal] = useState(false);
  const defaultDerivative = getDefaultInstrumentType();
  const defaultAsset = getDefaultTradingAsset();
  const savedMarket = getLastViewedMarket();

  const [market, setMarket] = useState<IMarket>({
    asset: (savedMarket?.asset as AssetResponse) || defaultAsset,
    derivative: savedMarket?.derivative || defaultDerivative,
  });

  useEffect(() => {
    setLastViewedMarket(market);
  }, [market]);

  return (
    <MarketContext.Provider
      value={{
        market,
        setMarket,
        showCommandModal,
        setShowCommandModal,
      }}
    >
      {children}
    </MarketContext.Provider>
  );
}
